import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const DISCIPLINES = {
  'brazilian-jiu-jitsu': {
    slug: 'brazilian-jiu-jitsu',
    title: 'Brazilian Jiu-Jitsu',
    eyebrow: 'The art of control',
    seo_title: 'Brazilian Jiu-Jitsu Private Training | DojoDash',
    seo_desc:
      'Learn jiu-jitsu privately with a coach who meets you where you are. Survive, control, and build a game of your own.',
    hero_image:
      'https://images.unsplash.com/photo-1747331796135-0e2354a712e4?w=1600',
    category_id: 'jiujitsu',
    paragraphs: [
      "Jiu-jitsu is the art of control. It's how a smaller, calmer person learns to neutralize someone bigger and stronger, not with strength, but with leverage, timing, and technique. On the ground, size stops mattering the way you think it does. Skill takes over.",
      "You'll start by learning to survive: how to stay safe, escape bad positions, and move with purpose instead of panic. From there you'll build real control by holding dominant positions, passing your partner's guard, and understanding submissions. Every session you'll feel a little more comfortable in situations that used to feel chaotic.",
      "This isn't about fighting. It's about problem-solving under pressure, one position at a time. You'll train privately with a coach who meets you where you are, whether you've never stepped on a mat or you're sharpening what you already know.",
      "Progress is real and you'll feel it, going from surviving, to controlling, to expressing a game that's entirely your own.",
    ],
  },
  'self-defense': {
    slug: 'self-defense',
    title: 'Self-Defense',
    eyebrow: 'The best fight is the one you never have',
    seo_title: 'Self-Defense Private Training | DojoDash',
    seo_desc:
      'Real-world self-defense training built around awareness, composure, and practical technique. Coaches come to you.',
    hero_image:
      'https://images.unsplash.com/photo-1611077479643-5b3c01381f9e?w=1600',
    category_id: 'self_defense',
    paragraphs: [
      'The best fight is the one you never have. Real self-defense starts long before anything physical, with awareness, confidence, and the ability to stay calm when your instincts are screaming.',
      "You'll learn to read situations, avoid trouble before it finds you, and carry yourself differently. Then you'll build the physical tools: how to create distance, break free from grabs, protect yourself if you end up on the ground, and stay composed under stress instead of freezing.",
      'This is practical, real-world training. Not choreography, not fantasy fighting. Every technique is built around what actually happens and what actually works. Your coach trains you at your own pace, in your own space, with an emphasis on calm confidence over aggression.',
      "The goal isn't to make you dangerous. It's to make you hard to victimize, someone who walks through the world alert, steady, and capable of protecting themselves and the people they love.",
    ],
  },
  'personal-training': {
    slug: 'personal-training',
    title: 'Personal Training',
    eyebrow: 'Build a body and mindset you can rely on',
    seo_title: 'Personal Training That Comes To You | DojoDash',
    seo_desc:
      'Private personal training built around your goals. A coach comes to you, holds you accountable, and takes you somewhere better.',
    hero_image:
      'https://images.unsplash.com/photo-1648542036561-e1d66a5ae2b1?w=1600',
    category_id: 'personal_training',
    paragraphs: [
      "Getting in shape isn't about punishing yourself. It's about building a body and a mindset you can rely on for life. Personal training with DojoDash meets you exactly where you are and takes you somewhere better, one session at a time.",
      "You'll start with the foundation: moving well, building consistency, and learning proper form so you train safely instead of getting hurt. From there you'll build real strength, endurance, and athletic control, the kind of fitness you can feel in everyday life, not just in the gym.",
      'Your coach comes to you, builds a plan around your goals, and holds you accountable in a way an app or a crowded gym never will. No intimidation, no guesswork, no wasted time.',
      "This is about more than looking better. It's discipline, energy, and confidence that carries into everything else you do, until being a fit, capable person is just part of who you are.",
    ],
  },
};

const DisciplineInfo = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isClient } = useAuth();
  const data = DISCIPLINES[slug];

  if (!data) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-bold text-white mb-3">Discipline not found</h1>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="border-zinc-700 text-zinc-300"
          data-testid="discipline-back-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Button>
      </div>
    );
  }

  const handleGetStarted = () => {
    if (isAuthenticated && isClient) {
      navigate(`/trainers?category=${data.category_id}`);
    } else if (isAuthenticated) {
      // Trainer viewing their own info page — send them to dashboard
      navigate('/dashboard');
    } else {
      navigate(`/register?category=${data.category_id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <SEO title={data.seo_title} description={data.seo_desc} />

      {/* Hero */}
      <section
        className="relative h-[42vh] min-h-[320px] flex items-end"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(9,9,11,0.55) 0%, rgba(9,9,11,0.95) 100%), url(${data.hero_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid={`discipline-hero-${data.slug}`}
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 pb-12 w-full">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6"
            data-testid={`discipline-back-${data.slug}`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <p className="text-[#C0A062] tracking-wide uppercase text-xs font-semibold mb-3">
            {data.eyebrow}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            {data.title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="space-y-6 text-zinc-300 text-base sm:text-lg leading-relaxed">
            {data.paragraphs.map((p, i) => (
              <p key={i} data-testid={`discipline-para-${data.slug}-${i}`}>
                {p}
              </p>
            ))}
          </div>

          <div className="mt-12 pt-10 border-t border-zinc-800 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button
              onClick={handleGetStarted}
              className="btn-primary px-6 py-6 text-base"
              data-testid={`discipline-get-started-${data.slug}`}
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <button
              type="button"
              onClick={() => navigate(`/trainers?category=${data.category_id}`)}
              className="text-sm text-zinc-400 hover:text-[#C0A062]"
              data-testid={`discipline-browse-coaches-${data.slug}`}
            >
              Or browse coaches first →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DisciplineInfo;
